import { useState, useEffect } from "react";
import axios from "axios";

/**
 * Custom hook that encapsulates all data-fetching logic for the timetable.
 *
 * Returns:
 *   - schedule: the raw schedule response from the API
 *   - weekDate: { start, end, dates[], iso_dates[] }
 *   - weekOffset: current week offset (int)
 *   - setWeekOffset: setter for weekOffset
 *   - locations: array of { id, name, address, description }
 *   - allDayEvents: all-day events overlapping the displayed week
 *   - notification: { message, type } | null
 *   - setNotification: setter for notification
 *   - error: error object | null
 */
const useTimetableData = () => {
  const [weekOffset, setWeekOffset] = useState(0);
  const [schedule, setSchedule] = useState([]);
  const [weekDate, setWeekDate] = useState({});
  const [locations, setLocations] = useState([]);
  const [allDayEvents, setAllDayEvents] = useState([]);
  const [notification, setNotification] = useState(null);
  const [error, setError] = useState(null);

  const base = process.env.REACT_APP_BACKEND_ADDRESS;

  // Fetch available locations once on mount
  useEffect(() => {
    axios
      .get(`${base}/lesson-type-locations/public`)
      .then((response) => {
        const sorted = [...response.data].sort((a, b) =>
          a.name.localeCompare(b.name, "pl")
        );
        setLocations(sorted);
      })
      .catch((err) => {
        console.error("Failed to fetch locations:", err);
        // Non-fatal: timetable still works, just no location tabs
      });
  }, [base]);

  // Fetch week date whenever weekOffset changes
  useEffect(() => {
    axios
      .get(`${base}/week_date/?week_offset=${weekOffset}`)
      .then((response) => {
        setWeekDate({ ...response.data });
      })
      .catch(setError);
  }, [weekOffset, base]);

  // Fetch schedule whenever weekOffset or notification changes
  useEffect(() => {
    axios
      .get(
        `${base}/lesson_schedule/?mode=hour_by_hour&flat=False&week_offset=${weekOffset}`
      )
      .then((response) => {
        setSchedule(response.data);
        setError(null);
      })
      .catch((err) => {
        console.error("Schedule API error:", err.response?.data || err.message);
        setError(err);
      });
  }, [weekOffset, notification, base]);

  // Fetch all-day events for exactly the days the timetable is showing. The
  // backend may auto-advance the week, so key off the returned dates rather
  // than recomputing them from weekOffset.
  const isoDates = weekDate.iso_dates;
  const firstDay = isoDates && isoDates[0];
  const lastDay = isoDates && isoDates[isoDates.length - 1];

  useEffect(() => {
    if (!firstDay || !lastDay) return;
    let cancelled = false;
    axios
      .get(`${base}/events/all-day?start_date=${firstDay}&end_date=${lastDay}`)
      .then((response) => {
        if (!cancelled) setAllDayEvents(response.data || []);
      })
      .catch(() => {
        // Non-fatal: the lesson grid still renders without the event bars.
        if (!cancelled) setAllDayEvents([]);
      });
    return () => {
      cancelled = true;
    };
  }, [firstDay, lastDay, base]);

  // Auto-clear notification after 20 seconds
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 20000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  return {
    schedule,
    weekDate,
    weekOffset,
    setWeekOffset,
    locations,
    allDayEvents,
    notification,
    setNotification,
    error,
  };
};

export default useTimetableData;
